import React from 'react'
import { Helmet } from 'react-helmet'
import { helmetJsonLdProp } from 'react-schemaorg'
import { WebPage, BreadcrumbList } from 'schema-dts'
import { useTranslation } from 'gatsby-plugin-react-i18next'

import { LandingPage } from '../../types/landingPage'
import useUrlPathBuilder from '../../hooks/useUrlPathBuilder'
import useBrand from '../../hooks/useBrand'
import OpenGraphSEO, { TwitterSEO } from './OpenGraphSEO'
import { LandingPageV2 } from '../../types/landingPageV2'
import { FAQPage } from '../../types/faq'
import { ReviewPage } from '../../types/review'
import insertHandlebarsTemplate from '../../utils/insertHandlebarsTemplate'
import { ProductPage } from '../../types/productPageV2'
import AboutPageSEO from './AboutPageSEO'
import { PageType } from '../../types/page'
import ContactSEO from './ContactSEO'
import useHandlebarsContext from '../../hooks/useHandlebarsContext'
import NoIndex from './NoIndex'
import useSiteData from '../../hooks/useSiteData'

interface Props {
  landingPage: LandingPage | LandingPageV2 | FAQPage | ReviewPage | ProductPage
  shouldRenderNoIndex: boolean
}

const LandingPageSEO: React.FC<Props> = ({
  landingPage,
  shouldRenderNoIndex,
}) => {
  const { urlPathBuilder, baseURL } = useUrlPathBuilder()
  const brand = useBrand()
  const site = useSiteData()
  const handlebarsContext = useHandlebarsContext()
  let slugToUse = landingPage.slug

  if ('fields' in landingPage && landingPage.fields) {
    slugToUse = landingPage.fields?.parsedSlug
  }

  const canonicalURL =
    landingPage.seo?.canonicalUrl ?? urlPathBuilder([slugToUse], { baseURL })
  const pageTitle = insertHandlebarsTemplate(
    landingPage.seo?.pageTitle ??
      landingPage.seo?.openGraphTitle ??
      landingPage.title,
    handlebarsContext
  )
  const description = insertHandlebarsTemplate(
    landingPage.seo?.description ?? landingPage.seo?.openGraphDescription,
    handlebarsContext
  )
  const {
    i18n: { language },
  } = useTranslation()

  return (
    <>
      {landingPage.seo?.pageType === PageType.About && (
        <AboutPageSEO landingPage={landingPage} />
      )}
      {landingPage.seo?.pageType === PageType.Contact && (
        <ContactSEO landingPage={landingPage} />
      )}
      <Helmet
        script={[
          helmetJsonLdProp<WebPage>({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            '@id': urlPathBuilder([slugToUse], {
              baseURL,
              hash: 'webpage',
            }),
            name: landingPage.seo?.pageTitle ?? landingPage.title,
            description: insertHandlebarsTemplate(
              landingPage.seo?.description ?? undefined,
              handlebarsContext
            ),
            url: urlPathBuilder([slugToUse], { baseURL }),
            datePublished: landingPage.createdAt,
            dateModified: landingPage.updatedAt,
            isPartOf: {
              '@id': urlPathBuilder([], {
                baseURL,
                hash: 'website',
              }),
            },
            about: {
              '@id': urlPathBuilder([], {
                baseURL,
                hash: 'organization',
              }),
            },
            potentialAction: [
              {
                '@type': 'ReadAction',
                target: [canonicalURL],
              },
            ],
            inLanguage: language,
          }),
          helmetJsonLdProp<BreadcrumbList>({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                item: {
                  '@type': 'WebPage',
                  '@id': urlPathBuilder([], { baseURL }),
                  url: urlPathBuilder([], { baseURL }),
                  name: 'Home Page',
                },
              },
              {
                '@type': 'ListItem',
                position: 2,
                item: {
                  '@type': 'WebPage',
                  '@id': urlPathBuilder([slugToUse], { baseURL }),
                  url: urlPathBuilder([slugToUse], { baseURL }),
                  name: landingPage.title ?? landingPage.seo?.pageTitle,
                },
              },
            ],
          }),
        ]}
      >
        <title>{pageTitle}</title>
        {description && <meta name="description" content={description} />}
        <link rel="canonical" href={canonicalURL} />
      </Helmet>
      <OpenGraphSEO
        seo={landingPage.seo}
        url={canonicalURL}
        title={pageTitle}
        description={description}
      />
      <TwitterSEO
        description={description}
        data={{
          'Written by': `${brand.name} Team`,
        }}
      />
      {shouldRenderNoIndex && (
        <NoIndex seo={landingPage.seo} slug={slugToUse} />
      )}
    </>
  )
}

export default LandingPageSEO
